import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import type { NoticeBlock } from './notice-fidelity.models';

/** Transient insertion intentions only; the Page owner retains source and caret authority. */
@Component({
  selector: 'phms-notice-insertion-panel',
  standalone: true,
  imports: [FormsModule, UiButtonDirective, UiControlDirective],
  templateUrl: './notice-insertion-panel.component.html',
  styleUrl: './notice-insertion-panel.component.scss',
})
export class NoticeInsertionPanelComponent {
  @Input() mode: 'token' | 'block' = 'token';
  @Input() disabled = false;
  @Input() tokenKind: 'field' | 'term' = 'field';
  @Input() tokenCode = '';
  @Input() tokenFormat = '';
  @Input() tokenSearch = '';
  /** Already filtered against the current catalog by the source owner. */
  @Input() tokenOptions: readonly { code: string; label: string }[] = [];
  @Input() tokenFormats: readonly string[] = [];
  @Input() insertionKind: NoticeBlock['kind'] = 'paragraph';
  @Input() insertionPosition: 'before' | 'after' = 'after';
  @Input() canAddBlock = false;
  @Input() canAddTable = false;
  @Input() blocksUsed = 0;
  @Input() blockLimit = 0;
  @Input() selectedLabel = '';
  @Output() readonly tokenKindChange = new EventEmitter<'field' | 'term'>();
  @Output() readonly tokenCodeChange = new EventEmitter<string>();
  @Output() readonly tokenFormatChange = new EventEmitter<string>();
  @Output() readonly tokenSearchChange = new EventEmitter<string>();
  @Output() readonly insertionKindChange = new EventEmitter<
    NoticeBlock['kind']
  >();
  @Output() readonly insertionPositionChange = new EventEmitter<
    'before' | 'after'
  >();
  @Output() readonly insertToken = new EventEmitter<void>();
  @Output() readonly insertBlock = new EventEmitter<void>();
  @Output() readonly closed = new EventEmitter<void>();
  @ViewChild('initialControl')
  private initialControl?: ElementRef<HTMLInputElement | HTMLSelectElement>;

  get selectedToken(): { code: string; label: string } | undefined {
    return this.tokenOptions.find((option) => option.code === this.tokenCode);
  }

  get canInsertToken(): boolean {
    return (
      this.mode === 'token' &&
      !this.disabled &&
      !!this.selectedToken &&
      (this.tokenFormats.length
        ? this.tokenFormats.includes(this.tokenFormat)
        : this.tokenFormat === '')
    );
  }

  get canInsertBlock(): boolean {
    return (
      this.mode === 'block' &&
      !this.disabled &&
      this.canAddBlock &&
      !!this.selectedLabel &&
      ['paragraph', 'box', 'repeat', 'pageBreak'].includes(
        this.insertionKind,
      ) &&
      ['before', 'after'].includes(this.insertionPosition) &&
      (this.insertionKind !== 'repeat' || this.canAddTable)
    );
  }

  changeTokenKind(kind: string): void {
    if (
      this.mode === 'token' &&
      !this.disabled &&
      (kind === 'field' || kind === 'term')
    )
      this.tokenKindChange.emit(kind);
  }

  changeTokenCode(code: string): void {
    if (
      this.mode === 'token' &&
      !this.disabled &&
      this.tokenOptions.some((option) => option.code === code)
    )
      this.tokenCodeChange.emit(code);
  }

  changeTokenFormat(format: string): void {
    if (
      this.mode === 'token' &&
      !this.disabled &&
      this.tokenFormats.includes(format)
    )
      this.tokenFormatChange.emit(format);
  }

  changeTokenSearch(search: string): void {
    if (this.mode === 'token' && !this.disabled)
      this.tokenSearchChange.emit(search);
  }

  changeInsertionKind(kind: string): void {
    if (
      this.mode === 'block' &&
      !this.disabled &&
      ['paragraph', 'box', 'repeat', 'pageBreak'].includes(kind) &&
      (kind !== 'repeat' || this.canAddTable)
    )
      this.insertionKindChange.emit(kind as NoticeBlock['kind']);
  }

  changeInsertionPosition(position: string): void {
    if (
      this.mode === 'block' &&
      !this.disabled &&
      (position === 'before' || position === 'after')
    )
      this.insertionPositionChange.emit(position);
  }

  requestToken(): void {
    if (this.canInsertToken) this.insertToken.emit();
  }

  requestBlock(): void {
    if (this.canInsertBlock) this.insertBlock.emit();
  }

  close(event?: KeyboardEvent): void {
    if (event && (event.key !== 'Escape' || event.isComposing)) return;
    event?.preventDefault();
    event?.stopPropagation();
    this.closed.emit();
  }

  focus(): boolean {
    const control = this.initialControl?.nativeElement;
    if (this.disabled || !control?.isConnected || control.disabled)
      return false;
    const controls = control.closest<HTMLElement>('.insertion-controls');
    if (controls) {
      controls.scrollTop = 0;
      controls.scrollLeft = 0;
    }
    control.focus({ preventScroll: true });
    return control.ownerDocument.activeElement === control;
  }
}
